import React, { useEffect, useRef, useState } from "react";
import { format } from "@flasd/whatsapp-formatting";
import emojiData from "emoji.json";

const WhatsappEditor = ({ value = "", onChange }) => {
    const editorRef = useRef(null);
    const savedRangeRef = useRef(null);

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    /* --------------------------------
       Sync from parent (EDIT MODE ONLY)
    -------------------------------- */
    useEffect(() => {
        if (!editorRef.current) return;

        // Do NOT touch while typing
        if (document.activeElement === editorRef.current) return;

        const html = format(value || "");
        if (editorRef.current.innerHTML !== html) {
            editorRef.current.innerHTML = html;
        }
    }, [value]);

    /* --------------------------------
       Caret helpers
    -------------------------------- */
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
            savedRangeRef.current = sel.getRangeAt(0);
        }
    };

    const restoreSelection = () => {
        const sel = window.getSelection();
        if (sel && savedRangeRef.current) {
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
        }
    };

    /* --------------------------------
       Prevent new line (ENTER)
    -------------------------------- */
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
        }
    };

    const htmlToWhatsapp = (html) => {
        if (!html) return "";

        html = html.replace(/<\s*(b|strong)\s*>/gi, "*");
        html = html.replace(/<\s*\/\s*(b|strong)\s*>/gi, "*");

        html = html.replace(/<\s*(i|em)\s*>/gi, "_");
        html = html.replace(/<\s*\/\s*(i|em)\s*>/gi, "_");

        html = html.replace(/<\s*(s|strike|del)\s*>/gi, "~");
        html = html.replace(/<\s*\/\s*(s|strike|del)\s*>/gi, "~");

        html = html.replace(/<br\s*\/?>/gi, "\n");
        html = html.replace(/<\/div>/gi, "\n");
        html = html.replace(/<div>/gi, "");

        html = html.replace(/<[^>]+>/g, "");

        const ta = document.createElement("textarea");
        ta.innerHTML = html;
        return ta.value;
    };



    /* --------------------------------
       Handle typing
    -------------------------------- */
    const handleInput = () => {
        if (!editorRef.current) return;

        const html = editorRef.current.innerHTML;
        const whatsappText = htmlToWhatsapp(html);

        onChange(whatsappText);
    };


    /* --------------------------------
       Wrap selection
    -------------------------------- */
    const wrapSelection = (symbol) => {
        restoreSelection();

        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        const text = range.toString();
        if (!text) return;

        range.deleteContents();
        range.insertNode(document.createTextNode(`${symbol}${text}${symbol}`));
        range.collapse(false);

        handleInput();
    };

    const ensureCaretInEditor = () => {
        const editor = editorRef.current;
        if (!editor) return;

        editor.focus();

        const sel = window.getSelection();

        // If no valid selection or selection is outside editor
        if (!sel || !sel.rangeCount || !editor.contains(sel.anchorNode)) {
            const range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false); // move caret to END
            sel.removeAllRanges();
            sel.addRange(range);
            savedRangeRef.current = range;
        }
    };


    /* --------------------------------
       Insert emoji
    -------------------------------- */
    const insertEmoji = (emoji) => {
        ensureCaretInEditor();
        restoreSelection();

        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        range.insertNode(document.createTextNode(emoji));
        range.collapse(false);

        saveSelection();
        handleInput();
    };


    const handlePaste = (e) => {
        e.preventDefault();

        ensureCaretInEditor();
        restoreSelection();

        const htmlData = e.clipboardData?.getData("text/html");
        const textData = e.clipboardData?.getData("text/plain") || "";

        const whatsappText = htmlData
            ? htmlToWhatsapp(htmlData)
            : textData.replace(/\u00A0/g, " ");

        const formattedHtml = format(whatsappText);


        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        range.deleteContents();

        // Insert formatted HTML safely
        const temp = document.createElement("div");
        temp.innerHTML = formattedHtml;

        const frag = document.createDocumentFragment();
        let node;
        while ((node = temp.firstChild)) {
            frag.appendChild(node);
        }

        range.insertNode(frag);
        range.collapse(false);

        saveSelection();
        handleInput();
    };



    return (
        <div className="wa-editor-container">
            {/* Toolbar (INSIDE editor box) */}
            <div className="wa-toolbar">
                <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelection("*")}
                >
                    <b>B</b>
                </button>

                <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelection("_")}
                >
                    <i>I</i>
                </button>

                <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => wrapSelection("~")}
                >
                    <s>S</s>
                </button>

                {/* Emoji button wrapper */}
                <div className="wa-emoji-trigger">
                    <button
                        className="wa-icon-btn"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            ensureCaretInEditor();
                            saveSelection();
                        }}
                        onClick={() => setShowEmojiPicker((v) => !v)}
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                            <line x1="9" y1="9" x2="9.01" y2="9" />
                            <line x1="15" y1="9" x2="15.01" y2="9" />
                        </svg>
                    </button>


                    {showEmojiPicker && (
                        <>
                        <div className="e-emoji-bd" onClick={()=>setShowEmojiPicker(false)}></div>
                        <div className="wa-emoji-picker">
                            {emojiData.slice(0, 300).map((e) => (
                                <span
                                    key={e.codes}
                                    onMouseDown={(ev) => ev.preventDefault()}
                                    onClick={() => {
                                        insertEmoji(e.char);
                                        setShowEmojiPicker(false);
                                    }}
                                >
                                    {e.char}
                                </span>
                            ))}
                        </div>
                        </>
                    )}
                </div>
            </div>


            {/* Editor */}
            <div
                ref={editorRef}
                className="wa-editor"
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
                onPaste={handlePaste}
            />
        </div>
    );
};

export default WhatsappEditor;
